import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { createInventoryStock } from '../../../services/inventoryStockService';
import { fetchInventoryProducts } from '../../../services/inventoryProductsService';

const AddStock: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>(); // product_id (optional)

  const [product_id, setProductId] = useState<string | undefined>(id);
  const [products, setProducts] = useState<
    { product_id: number; product_name: string }[]
  >([]);
  const [stockEntries, setStockEntries] = useState([
    { location: '', quantity: 0 }
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(!id); // Load products only if ID isn't provided

  // Fetch all products if no product_id is given
  useEffect(() => {
    if (!id) {
      const loadProducts = async () => {
        try {
          const data = await fetchInventoryProducts();
          setProducts(data);
        } catch (error) {
          setError('Failed to load products.');
        } finally {
          setLoadingProducts(false);
        }
      };
      loadProducts();
    }
  }, [id]);

  // Handle stock input changes
  const handleStockChange = (
    index: number,
    field: 'location' | 'quantity',
    value: string | number
  ) => {
    setStockEntries(prevStock =>
      prevStock.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const addStockEntry = () => {
    setStockEntries([...stockEntries, { location: '', quantity: 0 }]);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product_id) {
      setError('Please select a product.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      await Promise.all(
        stockEntries.map(stock =>
          createInventoryStock({ ...stock, product_id })
        )
      );

      alert('Stock added successfully!');
      navigate('/inventory/products/all-products');
    } catch (error) {
      setError('Error submitting stock. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>Add Stock</h2>
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        {/* Product Selection (Only if no product_id in URL) */}
        {!id && (
          <Form.Group>
            <Form.Label>Select Product</Form.Label>
            <Form.Select
              value={product_id}
              onChange={e => setProductId(e.target.value)}
              disabled={loadingProducts || submitting}
            >
              <option value="">Select a Product</option>
              {products.map(product => (
                <option key={product.product_id} value={product.product_id}>
                  {product.product_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* Stock Entries */}
        <h3>Stock Entries</h3>
        {stockEntries.map((stock, index) => (
          <div key={index} className="mb-3 p-3 border rounded">
            <Form.Group>
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                value={stock.location}
                onChange={e =>
                  handleStockChange(index, 'location', e.target.value)
                }
                required
                disabled={submitting}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Quantity</Form.Label>
              <Form.Control
                type="number"
                value={stock.quantity}
                onChange={e =>
                  handleStockChange(index, 'quantity', parseInt(e.target.value))
                }
                required
                disabled={submitting}
              />
            </Form.Group>
          </div>
        ))}

        <Button
          type="button"
          className="mt-2"
          onClick={addStockEntry}
          disabled={submitting}
        >
          + Add Stock Entry
        </Button>

        <Button type="submit" className="mt-3" disabled={submitting}>
          {submitting ? <Spinner animation="border" size="sm" /> : 'Add Stock'}
        </Button>
      </Form>
    </div>
  );
};

export default AddStock;
